import React, { useEffect, useState, useRef, useImperativeHandle, forwardRef } from 'react';
import { Button, Input, Table } from 'reactstrap';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4maps from '@amcharts/amcharts4/maps';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import cities from './mock';
import roteNdaoGeo from '../../assets/jsonData/kab_Rote.json';
import TulufaliLogo from '../Icons/tulufali_logo.svg';
import s from './am4chartMap.module.scss';
import './tooltip.css';
import { DESA_BANTUAN_TAHUN, BANTUAN_LIST_TAHUN } from '../../utils';
import { normalizeForMatch, resolveKecamatanIncludedFilter } from '../../utils/mapGeoMerge';
import axios from 'axios';

const TAGLINE = 'Tata Kelola Bantuan Terintegrasi Untuk Penanggulangan Kemiskinan Rote Ndao';

const formatRupiah = (n) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n || 0);
};

const Am4chartMap = forwardRef(function Am4chartMap(
  {
    tahun = '2024',
    fixedBantuan = null,
    compactHeader = false,
    hidePerDesaTable = false,
    mapDomId: mapDomIdProp = null,
    hideMapChrome = false,
    customizationEmbed = false,
    externalBantuan = null,
    /**
     * Opsional (halaman pengaturan peta): samakan tingkat peta & filter kecamatan dengan peta DTSEN.
     * { mapLevel, kecamatanIncludedKeys, colors?: { none, stroke? } }
     */
    layoutSettings = null,
  },
  ref
) {
  const [listDesa, setListDesa] = useState([]);
  const [listBantuan, setListBantuan] = useState([]);
  const [internalBantuan, setInternalBantuan] = useState('all');
  const isFixed = Boolean(fixedBantuan);
  const isExternal = Boolean(externalBantuan);
  const selectedBantuan = isFixed ? fixedBantuan : isExternal ? externalBantuan.value : internalBantuan;
  const mapDomId = mapDomIdProp || (isFixed ? 'mapBantuanDetail' : 'map');
  const [isLoadingMap, setIsLoadingMap] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const am4ChartRef = useRef(null);
  const mapWrapRef = useRef(null);
  const tahunRef = useRef(tahun);
  const features = roteNdaoGeo.features;
  const [mapCombine, setMapCombine] = useState();
  const [danaThresholds, setDanaThresholds] = useState({ threshold1: 0, threshold2: 0 });
  const pdfExportRef = useRef({ mode: 'desa', rows: [] });
  const [bantuanTableSnapshot, setBantuanTableSnapshot] = useState({ mode: 'desa', rows: [] });

  const desaToKec = {};
  (roteNdaoGeo.features || []).forEach((f) => {
    const d = String(f.properties?.DESA || '').trim().toUpperCase();
    if (d) desaToKec[d] = f.properties?.KECAMATAN || '';
  });
  const getKecamatan = (desa) => desaToKec[String(desa || '').trim().toUpperCase()] || '-';

  const DANA_COLORS = {
    none: '#e0e0e0',
    low: '#ffe082',
    medium: '#f5bf23',
    high: '#b38600',
  };

  const combineData = () => {
    const mapLevel = layoutSettings?.mapLevel === 'kecamatan' ? 'kecamatan' : 'desa';
    const colorsNone = layoutSettings?.colors?.none || DANA_COLORS.none;

    const allKecKeysFromGeo = new Set();
    for (let gi = 0; gi < features.length; gi++) {
      const kk = normalizeForMatch(features[gi].properties?.KECAMATAN);
      if (kk) allKecKeysFromGeo.add(kk);
    }
    const kecFilter = layoutSettings
      ? resolveKecamatanIncludedFilter(mapLevel, layoutSettings.kecamatanIncludedKeys, allKecKeysFromGeo)
      : { active: false, set: null };

    const desaLookup = new Map();
    listDesa.forEach((item) => {
      const key = String(item?.desa || '').toUpperCase().trim();
      if (key) desaLookup.set(key, item);
    });

    const kecMetric = new Map();
    listDesa.forEach((row) => {
      const kRaw =
        row.kecamatan != null && String(row.kecamatan).trim() !== ''
          ? String(row.kecamatan).trim()
          : getKecamatan(row.desa);
      const k = normalizeForMatch(kRaw);
      if (!k) return;
      const prev = kecMetric.get(k) || {
        total_uang: 0,
        jumlah_penerima: 0,
        total_desil12: 0,
        label: kRaw || k,
      };
      prev.total_uang += Number(row.total_uang) || 0;
      prev.jumlah_penerima += Number(row.jumlah_penerima) || 0;
      prev.total_desil12 += Number(row.total_desil12) || 0;
      if (kRaw && !prev.label) prev.label = kRaw;
      kecMetric.set(k, prev);
    });

    const valuesForTiers = [];
    if (mapLevel === 'kecamatan') {
      kecMetric.forEach((m, k) => {
        if (kecFilter.active && kecFilter.set && !kecFilter.set.has(k)) return;
        if (m.total_uang > 0) valuesForTiers.push(m.total_uang);
      });
    } else {
      listDesa.forEach((d) => {
        const kecRow = normalizeForMatch(getKecamatan(d.desa));
        if (kecFilter.active && kecFilter.set && kecRow && !kecFilter.set.has(kecRow)) return;
        const v = Number(d.total_uang) || 0;
        if (v > 0) valuesForTiers.push(v);
      });
    }
    valuesForTiers.sort((a, b) => a - b);

    let threshold1 = 0;
    let threshold2 = 0;
    if (valuesForTiers.length >= 3) {
      const i1 = Math.floor(valuesForTiers.length / 3);
      const i2 = Math.floor((2 * valuesForTiers.length) / 3);
      threshold1 = valuesForTiers[i1];
      threshold2 = valuesForTiers[i2];
    } else if (valuesForTiers.length === 2) {
      threshold1 = valuesForTiers[0];
      threshold2 = valuesForTiers[1];
    } else if (valuesForTiers.length === 1) {
      threshold1 = threshold2 = valuesForTiers[0];
    }

    setDanaThresholds({ threshold1, threshold2 });

    const getTierColor = (totalUang) => {
      if (!totalUang || totalUang <= 0) return colorsNone;
      if (totalUang <= threshold1) return DANA_COLORS.low;
      if (totalUang <= threshold2) return DANA_COLORS.medium;
      return DANA_COLORS.high;
    };

    const desaku = [];
    for (let i = 0; i < features.length; i++) {
      const desa = features[i];
      const desaName = (desa.properties?.DESA || '').toUpperCase().trim();
      const kecKey = normalizeForMatch(desa.properties?.KECAMATAN);
      const dimmedKec =
        kecFilter.active && kecFilter.set && kecKey && !kecFilter.set.has(kecKey);

      if (mapLevel === 'kecamatan') {
        const m = kecMetric.get(kecKey) || {
          total_uang: 0,
          jumlah_penerima: 0,
          total_desil12: 0,
          label: desa.properties?.KECAMATAN || '',
        };
        const tu = dimmedKec ? 0 : m.total_uang;
        desa.properties.penerimaBantuan = dimmedKec ? 0 : m.jumlah_penerima;
        desa.properties.totalUang = tu;
        desa.properties.totalDesil12 = dimmedKec ? 0 : m.total_desil12;
        desa.properties.totalUangFormatted = formatRupiah(dimmedKec ? 0 : m.total_uang);
        desa.properties.color = dimmedKec ? colorsNone : getTierColor(tu);
        desa.properties.tooltipBantuanSubtitle = dimmedKec
          ? ''
          : 'Nilai agregat per kecamatan (satu warna per kecamatan)';
        desa.properties._bantuanLevel = 'kecamatan';
      } else {
        desa.properties.tooltipBantuanSubtitle = '';
        desa.properties._bantuanLevel = 'desa';
        if (dimmedKec) {
          desa.properties.penerimaBantuan = 0;
          desa.properties.totalUang = 0;
          desa.properties.totalDesil12 = 0;
          desa.properties.totalUangFormatted = formatRupiah(0);
          desa.properties.color = colorsNone;
        } else {
          const bantuanDesa = desaLookup.get(desaName);
          if (bantuanDesa) {
            const totalUang = Number(bantuanDesa.total_uang) || 0;
            desa.properties.penerimaBantuan = Number(bantuanDesa.jumlah_penerima) || 0;
            desa.properties.totalUang = totalUang;
            desa.properties.totalDesil12 = Number(bantuanDesa.total_desil12) || 0;
            desa.properties.totalUangFormatted = formatRupiah(bantuanDesa.total_uang);
            desa.properties.color = getTierColor(totalUang);
          } else {
            desa.properties.penerimaBantuan = 0;
            desa.properties.totalUang = 0;
            desa.properties.totalDesil12 = 0;
            desa.properties.totalUangFormatted = formatRupiah(0);
            desa.properties.color = colorsNone;
          }
        }
      }

      desaku.push(desa);
    }

    setMapCombine({
      type: 'FeatureCollection',
      features: desaku,
    });

    let snapshot = { mode: 'desa', rows: [] };
    if (mapLevel === 'kecamatan') {
      const rows = [];
      kecMetric.forEach((m, k) => {
        if (kecFilter.active && kecFilter.set && !kecFilter.set.has(k)) return;
        rows.push({ kecKey: k, ...m });
      });
      rows.sort((a, b) => String(a.label || '').localeCompare(String(b.label || ''), 'id'));
      snapshot = { mode: 'kecamatan', rows };
    } else {
      const rows = listDesa.filter((row) => {
        const kr = normalizeForMatch(getKecamatan(row.desa));
        if (kecFilter.active && kecFilter.set && kr && !kecFilter.set.has(kr)) return false;
        return true;
      });
      snapshot = { mode: 'desa', rows };
    }
    pdfExportRef.current = snapshot;
    setBantuanTableSnapshot(snapshot);
  };

  const getListDesa = async (year, bantuan) => {
    if (!year) return;
    const bantuanParam = bantuan && bantuan !== 'all' ? `&bantuan=${encodeURIComponent(bantuan)}` : '';
    const url = DESA_BANTUAN_TAHUN + year + (bantuanParam ? '?' + bantuanParam.slice(1) : '');
    tahunRef.current = year;
    setIsLoadingMap(true);
    try {
      const res = await axios.get(url);
      if (tahunRef.current !== year) return;
      const data = res.data?.data || [];
      setListDesa(data);
    } catch (error) {
      if (tahunRef.current !== year) return;
      console.error('getListDesa:', error);
      setListDesa([]);
    } finally {
      if (tahunRef.current === year) setIsLoadingMap(false);
    }
  };

  const fetchListBantuan = (year) => {
    if (!year) return;
    axios.get(BANTUAN_LIST_TAHUN + year)
      .then((res) => {
        const data = res.data?.data || [];
        setListBantuan(Array.isArray(data) ? data : []);
      })
      .catch(() => setListBantuan([]));
  };

  useEffect(() => {
    if (isFixed || isExternal) return;
    setInternalBantuan('all');
    fetchListBantuan(tahun);
  }, [tahun, isFixed, isExternal]);

  useEffect(() => {
    getListDesa(tahun, selectedBantuan);
  }, [tahun, selectedBantuan]);

  const layoutKey = layoutSettings
    ? JSON.stringify({
        ml: layoutSettings.mapLevel,
        k: layoutSettings.kecamatanIncludedKeys,
        n: layoutSettings.colors?.none,
        s: layoutSettings.colors?.stroke,
      })
    : '';

  useEffect(() => {
    if (listDesa) {
      combineData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listDesa, layoutKey]);

  useEffect(() => {
    if (!mapCombine) return;

    if (am4ChartRef.current) {
      am4ChartRef.current.dispose();
      am4ChartRef.current = null;
    }

    const map = am4core.create(mapDomId, am4maps.MapChart);
    am4ChartRef.current = map;
    if (map.logo) map.logo.hide();
    setTimeout(() => { if (map.logo) map.logo.hide(); }, 500);
    map.maxZoomLevel = 4;
    map.maxPanOut = 0.75;
    map.geodata = mapCombine;
    map.percentHeight = 90;
    map.dy = 10;
      //   map.projection = new am4maps.projections.AlbersUsa();
      let polygonSeries = map.series.push(new am4maps.MapPolygonSeries());
      polygonSeries.useGeodata = true;
      map.homeZoomLevel = 1;
      map.zoomControl = new am4maps.ZoomControl();
      map.zoomControl.layout = 'horizontal';
      map.zoomControl.align = 'left';
      map.zoomControl.valign = 'bottom';
      map.zoomControl.dy = 30;
      map.zoomControl.dx = 30;
      map.zoomControl.contentHeight = 20;
      map.zoomControl.minusButton.background.fill = am4core.color('#C7D0FF');
      map.zoomControl.minusButton.background.stroke = am4core.color('white');
      map.zoomControl.minusButton.label.fontWeight = 600;
      map.zoomControl.minusButton.label.fontSize = 22;
      map.zoomControl.minusButton.scale = 0.75;
      map.zoomControl.minusButton.label.scale = 0.75;
      map.zoomControl.plusButton.background.fill = am4core.color('#C7D0FF');
      map.zoomControl.plusButton.background.stroke = am4core.color('#6979C9');
      map.zoomControl.plusButton.label.fontWeight = 600;
      map.zoomControl.plusButton.label.fontSize = 22;
      map.zoomControl.plusButton.label.align = 'center';
      map.zoomControl.plusButton.scale = 0.75;
      map.zoomControl.plusButton.label.scale = 0.75;
      map.zoomControl.plusButton.dx = 5;
      let plusButtonHoverState =
        map.zoomControl.plusButton.background.states.create('hover');
      plusButtonHoverState.properties.fill = am4core.color('#354D84');
      let minusButtonHoverState =
        map.zoomControl.minusButton.background.states.create('hover');
      minusButtonHoverState.properties.fill = am4core.color('#354D84');
      let polygonTemplate = polygonSeries.mapPolygons.template;

      polygonTemplate.tooltipHTML = `
    <div class="map-tooltip-card">
      <div class="map-tooltip-title"><strong>{DESA}</strong> <span class="map-tooltip-sub">· {KECAMATAN}</span></div>
      <div class="map-tooltip-caption">{tooltipBantuanSubtitle}</div>
      <div class="map-tooltip-body">
        <div class="map-tooltip-row">
          <span class="map-tooltip-label">Penerima</span>
          <span class="map-tooltip-value">{penerimaBantuan}</span>
        </div>
        <div class="map-tooltip-row">
          <span class="map-tooltip-label">Dana</span>
          <span class="map-tooltip-value map-tooltip-rupiah">{totalUangFormatted}</span>
        </div>
        <div class="map-tooltip-row map-tooltip-highlight">
          <span class="map-tooltip-label">Desil 1+2</span>
          <span class="map-tooltip-value">{totalDesil12}</span>
        </div>
      </div>
    </div>
    `;

      //

      //polygonTemplate.propertyFields.fill = 'SYMB';
      polygonSeries.events.on('datavalidated', () => {
        polygonSeries.mapPolygons.each((polygon) => {
          let color = polygon.dataItem.dataContext.color;
          polygon.fill = color;
        });
      });

      //

      //polygonTemplate.fill = am4core.color('red');
      polygonTemplate.stroke = am4core.color(layoutSettings?.colors?.stroke || 'white');
      let hs = polygonTemplate.states.create('hover');
      hs.properties.fill = am4core.color('blue');

      //

      //

      let citySeries = map.series.push(new am4maps.MapImageSeries());
      citySeries.data = cities;
      citySeries.dataFields.value = 'size';
      let city = citySeries.mapImages.template;
      city.nonScaling = true;
      city.propertyFields.latitude = 'latitude';
      city.propertyFields.longitude = 'longitude';
      let circle = city.createChild(am4core.Circle);
      circle.fill = am4core.color('#C7D0FF');
      circle.strokeWidth = 0;
      let circleHoverState = circle.states.create('hover');
      circleHoverState.properties.strokeWidth = 1;
      circle.tooltipText = '{tooltip}';
      circle.propertyFields.radius = 'size';

    return () => {
      if (am4ChartRef.current) {
        am4ChartRef.current.dispose();
        am4ChartRef.current = null;
      }
    };
    // layoutKey mem-fingerprint layoutSettings (termasuk colors.stroke)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapCombine, mapDomId, layoutKey]);

  const toSmallCaps = (t) => (t || '').toString().toLowerCase().replace(/(?:^|\s)\S/g, (c) => c.toUpperCase());

  const legendNoneColor = layoutSettings?.colors?.none || DANA_COLORS.none;

  const handleDownloadPdf = async () => {
    if (!mapWrapRef.current || isLoadingMap || !listDesa.length) return;
    setIsDownloadingPdf(true);
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      let y = 10;

      const addLogoAndSave = async () => {
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        doc.text('Pilihan: Tahun ' + tahun + (selectedBantuan !== 'all' ? ' — ' + selectedBantuan : ''), 14, y);
        y += 10;

        const mapCanvas = await html2canvas(mapWrapRef.current, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
        });
        const mapImgData = mapCanvas.toDataURL('image/png');
        const imgW = pageW - 28;
        const imgH = (mapCanvas.height * imgW) / mapCanvas.width;
        const maxH = pageH - y - 20;
        const finalH = Math.min(imgH, maxH);
        doc.addImage(mapImgData, 'PNG', 14, y, imgW, finalH);
        y += finalH + 12;

        doc.setFontSize(10);
        doc.setTextColor(30, 41, 59);
        const snap = pdfExportRef.current;
        const perKec = snap && snap.mode === 'kecamatan';
        doc.text(
          (perKec ? 'Data per Kecamatan — ' : 'Data per Desa — ') +
            'Distribusi Bantuan Tahun ' +
            tahun,
          14,
          y
        );
        y += 8;

        const tableBody = perKec
          ? (snap.rows || []).map((row, idx) => [
              String(idx + 1),
              String(row.label || row.kecKey || ''),
              String(row.jumlah_penerima ?? 0),
              formatRupiah(row.total_uang ?? 0),
              String(row.total_desil12 ?? 0),
            ])
          : (snap.rows || []).map((row, idx) => [
              String(idx + 1),
              (row.desa || '').toString(),
              getKecamatan(row.desa),
              String(row.jumlah_penerima ?? 0),
              formatRupiah(row.total_uang ?? 0),
              String(row.total_desil12 ?? 0),
            ]);
        doc.autoTable({
          startY: y,
          head: perKec
            ? [['No', 'Kecamatan', 'Penerima', 'Total Dana', 'Desil 1+2']]
            : [['No', 'Desa', 'Kecamatan', 'Penerima', 'Total Dana', 'Desil 1+2']],
          body: tableBody,
          theme: 'grid',
          headStyles: { fillColor: [25, 118, 210], fontSize: 8 },
          bodyStyles: { fontSize: 7 },
          columnStyles: perKec
            ? {
                0: { cellWidth: 12, halign: 'center' },
                1: { cellWidth: 52 },
                2: { cellWidth: 24, halign: 'right' },
                3: { cellWidth: 46, halign: 'right' },
                4: { cellWidth: 24, halign: 'right' },
              }
            : {
                0: { cellWidth: 10, halign: 'center' },
                1: { cellWidth: 38 },
                2: { cellWidth: 35 },
                3: { cellWidth: 22, halign: 'right' },
                4: { cellWidth: 42, halign: 'right' },
                5: { cellWidth: 22, halign: 'right' },
              },
          margin: { left: 14 },
          didDrawPage: () => {
            doc.setFontSize(8);
            doc.setTextColor(120, 120, 120);
            doc.text(`Unduh dari Tulufali - ${new Date().toLocaleDateString('id-ID')}`, 14, pageH - 8);
          },
        });

        const bantuanSlug = (selectedBantuan && selectedBantuan !== 'all')
          ? `-${String(selectedBantuan).replace(/\s+/g, '-').slice(0, 40)}`
          : '';
        doc.save(`Peta-Distribusi-Bantuan-${tahun}${bantuanSlug}-${new Date().toLocaleDateString('id-ID').replace(/\//g, '-')}.pdf`);
        setIsDownloadingPdf(false);
      };

      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const logoData = canvas.toDataURL('image/png');
          doc.addImage(logoData, 'PNG', 14, y, 10, 10);
          doc.setFontSize(7);
          doc.setTextColor(80, 80, 80);
          doc.text(TAGLINE, 25, y + 6, { maxWidth: pageW - 30 });
        } catch (_) {
          doc.setFontSize(7);
          doc.text(TAGLINE, 14, y + 5);
        }
        y += 18;
        addLogoAndSave();
      };
      img.onerror = () => {
        doc.setFontSize(7);
        doc.text(TAGLINE, 14, y + 5);
        y += 18;
        addLogoAndSave();
      };
      img.src = TulufaliLogo;
    } catch (err) {
      console.error('handleDownloadPdf:', err);
      setIsDownloadingPdf(false);
    }
  };

  const downloadPdfHandlerRef = useRef(handleDownloadPdf);
  downloadPdfHandlerRef.current = handleDownloadPdf;
  useImperativeHandle(ref, () => ({
    downloadPdf: () => Promise.resolve(downloadPdfHandlerRef.current()),
  }), []);

  const rootClass = [s.mapChart, customizationEmbed ? s.mapChartCustomization : '']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClass}>
      <div className={s.mapWrap} ref={mapWrapRef}>
        {!hideMapChrome && (
          <div className={s.mapStats}>
            <div className={s.mapStatsLeft}>
              {compactHeader && isFixed ? (
                <p className={s.mapStatsYear} style={{ margin: 0, opacity: 0.75 }}>
                  Tahun {tahun}
                </p>
              ) : (
                <>
                  <h6 className={s.mapStatsTitle}>SEBARAN BANTUAN PER DESA</h6>
                  <p className={s.mapStatsYear}>
                    Tahun {tahun}
                    {isFixed && fixedBantuan ? ` — ${fixedBantuan}` : ''}
                  </p>
                </>
              )}
            </div>
            <div className={s.mapSelectWrap} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {!isFixed && (
                <Input
                  type="select"
                  value={isExternal ? externalBantuan.value : internalBantuan}
                  onChange={(e) =>
                    isExternal ? externalBantuan.onChange(e.target.value) : setInternalBantuan(e.target.value)
                  }
                  className={s.mapSelect}
                >
                  <option value="all">Semua Bantuan</option>
                  {(isExternal ? externalBantuan.options : listBantuan).map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </Input>
              )}
              <Button
                color="light"
                size="sm"
                onClick={handleDownloadPdf}
                disabled={isDownloadingPdf || isLoadingMap || !listDesa.length}
                className={s.btnDownloadPdf}
              >
                <i className={`fa fa-file-pdf-o ${s.pdfIcon}`} aria-hidden="true" />
                {isDownloadingPdf ? ' ...' : ' PDF'}
              </Button>
            </div>
          </div>
        )}
        {isLoadingMap && (
          <div className={s.mapOverlay}>
            <div className={s.mapSpinner} />
            <span className={s.mapLoadingText}>Memuat data peta...</span>
          </div>
        )}
        <div className={s.map} id={mapDomId} />
        <div className={s.mapLegend}>
          <div className={s.legendItem}>
            <span className={s.legendColor} style={{ background: legendNoneColor }} />
            <span className={s.legendLabel}>Tidak ada bantuan sosial</span>
          </div>
          <div className={s.legendItem}>
            <span className={s.legendColor} style={{ background: DANA_COLORS.low }} />
            <span className={s.legendLabel}>
              {danaThresholds.threshold1 > 0
                ? `Rp 1 – ${formatRupiah(danaThresholds.threshold1)}`
                : '—'}
            </span>
          </div>
          <div className={s.legendItem}>
            <span className={s.legendColor} style={{ background: DANA_COLORS.medium }} />
            <span className={s.legendLabel}>
              {danaThresholds.threshold1 < danaThresholds.threshold2
                ? `${formatRupiah(danaThresholds.threshold1 + 1)} – ${formatRupiah(danaThresholds.threshold2)}`
                : '—'}
            </span>
          </div>
          <div className={s.legendItem}>
            <span className={s.legendColor} style={{ background: DANA_COLORS.high }} />
            <span className={s.legendLabel}>
              {danaThresholds.threshold2 > 0
                ? `> ${formatRupiah(danaThresholds.threshold2)}`
                : '—'}
            </span>
          </div>
        </div>
      </div>

      {listDesa.length > 0 && !hidePerDesaTable && (
        <div className={s.mapDataTableWrap}>
          <h6 className={s.mapDataTableTitle}>
            {compactHeader && isFixed
              ? bantuanTableSnapshot.mode === 'kecamatan'
                ? `Per kecamatan — ${tahun}`
                : `Per desa — ${tahun}`
              : bantuanTableSnapshot.mode === 'kecamatan'
                ? `Data per Kecamatan — Tahun ${tahun}${
                    selectedBantuan !== 'all' ? ` (${selectedBantuan})` : ''
                  }`
                : `Data per Desa — Tahun ${tahun}${
                    selectedBantuan !== 'all' ? ` (${selectedBantuan})` : ''
                  }`}
          </h6>
          <div className={s.mapDataTableScroll}>
            <Table responsive bordered size="sm" className={s.mapDataTable}>
              <thead>
                <tr>
                  <th className={s.thNo}>No</th>
                  {bantuanTableSnapshot.mode === 'kecamatan' ? (
                    <th>Kecamatan</th>
                  ) : (
                    <>
                      <th>Desa</th>
                      <th>Kecamatan</th>
                    </>
                  )}
                  <th className={s.thNum}>Penerima</th>
                  <th className={s.thNum}>Total Dana</th>
                  <th className={s.thNum}>Desil 1+2</th>
                </tr>
              </thead>
              <tbody>
                {bantuanTableSnapshot.mode === 'kecamatan'
                  ? bantuanTableSnapshot.rows.map((row, idx) => (
                      <tr key={row.kecKey || idx}>
                        <td className={s.thNo}>{idx + 1}</td>
                        <td>{toSmallCaps(row.label || row.kecKey)}</td>
                        <td className={s.thNum}>
                          {(row.jumlah_penerima ?? 0).toLocaleString('id-ID')}
                        </td>
                        <td className={s.thNum}>{formatRupiah(row.total_uang)}</td>
                        <td className={s.thNum}>
                          {(row.total_desil12 ?? 0).toLocaleString('id-ID')}
                        </td>
                      </tr>
                    ))
                  : bantuanTableSnapshot.rows.map((row, idx) => (
                      <tr key={`${row.desa}-${idx}`}>
                        <td className={s.thNo}>{idx + 1}</td>
                        <td>{toSmallCaps(row.desa)}</td>
                        <td>{toSmallCaps(getKecamatan(row.desa))}</td>
                        <td className={s.thNum}>
                          {(row.jumlah_penerima ?? 0).toLocaleString('id-ID')}
                        </td>
                        <td className={s.thNum}>{formatRupiah(row.total_uang)}</td>
                        <td className={s.thNum}>
                          {(row.total_desil12 ?? 0).toLocaleString('id-ID')}
                        </td>
                      </tr>
                    ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
});

Am4chartMap.displayName = 'Am4chartMap';

export default Am4chartMap;
